import React, { useEffect, useState } from 'react';
import { getTeamUserRanking, UserStatistics } from '../../services/statisticsService';
import { formatDistance, formatTime, formatPace, formatDate } from '../../utils/formatters';
import { FaUsers, FaMedal, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import './statistics.css';
import UserActivitiesDialog from '../../components/UserActivitiesDialog';

interface TeamUserRankingProps {
  teamId: number;
  startDateLocal: string;
  endDate: string;
}

const TeamUserRanking: React.FC<TeamUserRankingProps> = ({ teamId, startDateLocal, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userRanking, setUserRanking] = useState<UserStatistics[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchUserRanking = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getTeamUserRanking(teamId, startDateLocal, endDate);
        setUserRanking(data);
      } catch (err: any) {
        setError(err.message || 'Có lỗi xảy ra khi lấy bảng xếp hạng người dùng');
      } finally {
        setLoading(false);
      }
    };

    if (teamId) {
      fetchUserRanking();
    }
  }, [teamId, startDateLocal, endDate]);

  const handleOpenUserActivities = (userId: number, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  if (loading) {
    return (
      <div className="loading-state">
        <FaSpinner className="icon-spin" size={40} color="#20757A" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <FaExclamationTriangle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  if (!userRanking || userRanking.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <FaUsers />
        </div>
        <div className="empty-state-text">Không có dữ liệu xếp hạng cho đội này</div>
      </div>
    );
  }

  // Sắp xếp theo quãng đường giảm dần
  const sortedRanking = [...userRanking].sort((a, b) => b.totalDistance - a.totalDistance);

  return (
    <div className={`team-user-ranking ${theme}`}>
      <div className={`statistics-card ${theme}`}>
        <div className="statistics-card-header">
          <h2 className="statistics-card-title">
            <FaMedal /> Bảng xếp hạng thành viên
          </h2>
        </div>
        
        <table className="ranking-table">
          <thead>
            <tr>
              <th>Thứ hạng</th>
              <th>Thành viên</th>
              <th>Quãng đường</th>
              <th>Hoạt động</th>
              <th>Thời gian</th>
              <th>Nhịp độ TB</th>
              <th>Hoạt động gần nhất</th>
            </tr>
          </thead>
          <tbody>
            {sortedRanking.map((user, index) => (
              <tr 
                key={user.userId} 
                onClick={() => handleOpenUserActivities(user.userId, user.fullName)}
                style={{ cursor: 'pointer' }}
                className="member-row"
              >
                <td className="rank-cell">#{index + 1}</td>
                <td>
                  <div className="user-info">
                    {user.avatarUrl ? (
                      <img 
                        src={user.avatarUrl} 
                        alt={user.fullName}
                        className="user-avatar"
                      />
                    ) : (
                      <div className="user-avatar-placeholder">
                        {user.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                      </div>
                    )}
                    <span className="user-name">{user.fullName}</span>
                  </div>
                </td>
                <td className="distance-cell">{formatDistance(user.totalDistance)}</td>
                <td className="activity-count-cell">{user.activityCount}</td>
                <td>{formatTime(user.totalMovingTime)}</td>
                <td>{user.averagePace ? formatPace(user.averagePace) : '--'}</td>
                <td>{formatDate(user.lastActivityDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedUserId && (
        <UserActivitiesDialog
          userId={selectedUserId}
          userName={selectedUserName}
          startDateLocal={startDateLocal}
          endDate={endDate}
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
        />
      )}
    </div>
  );
};

export default TeamUserRanking; 